/**
 * Phase 9.8 (2026-05-24) — D-9.8-4 LOCKED ToolHistoryChip.
 *
 * Renders a single tool row from the voice history log. Collapsed view is
 * a compact chip `[tool_name → result_summary] Nms`; click expands to show
 * args + result (or error). Single-row expansion is controlled by the
 * parent (VoicePanel) via the `expanded` + `onToggle` props.
 *
 * Schema reference: VoiceTurn in stores/voiceStore.ts L640+. Phase 9.8
 * extends VoiceTurn additively with optional `tool` field.
 *
 * Testids (per R1 brief §1.2):
 *   voice-history-tool-chip-${idx}     — the closed chip
 *   voice-history-tool-expand-${idx}   — the click target
 *   voice-history-tool-args-${idx}     — args body (when expanded)
 *   voice-history-tool-result-${idx}   — result body (when expanded, success path)
 *   voice-history-tool-error-${idx}    — error body (when expanded, error path)
 */
import React from 'react';

import type { VoiceTurn } from '../../stores/voiceStore';
import { formatVoiceTimestamp } from '../../shared/format-voice-timestamp';

export interface ToolHistoryChipProps {
  turn: VoiceTurn;
  idx: number;
  expanded: boolean;
  onToggle: () => void;
}

export const ToolHistoryChip: React.FC<ToolHistoryChipProps> = ({
  turn,
  idx,
  expanded,
  onToggle,
}) => {
  const tool = turn.tool;
  // Defensive: if the row is tagged role:'tool' but lacks the structured
  // tool field, fall back to plain text rendering (shouldn't happen on
  // properly-shaped frames, but keeps the renderer safe under malformed
  // localStorage data).
  if (!tool) {
    return (
      <span className="text-vibe-text-secondary italic" data-testid={`voice-history-tool-chip-${idx}`}>
        [tool: malformed row]
      </span>
    );
  }
  const isError = Boolean(tool.error);
  const labelInner = isError
    ? `${tool.name} → error`
    : tool.result_summary
      ? `${tool.name} → ${tool.result_summary}`
      : `${tool.name} ran`;
  const chipColorClass = isError
    ? 'text-vibe-error bg-vibe-bg-tertiary border-vibe-error/40'
    : 'text-vibe-text bg-vibe-bg-tertiary border-vibe-border';
  const durationLabel = (
    typeof tool.duration_ms === 'number' && tool.duration_ms > 0
      ? ` ${tool.duration_ms}ms`
      : ''
  );
  return (
    <span>
      <span
        className="text-vibe-text-secondary text-[10px] tabular-nums mr-1.5"
        data-testid={`voice-history-tool-time-${idx}`}
      >
        {formatVoiceTimestamp(turn.timestamp)}
      </span>
      <button
        type="button"
        onClick={onToggle}
        className={`inline-block px-1.5 py-0 rounded border text-[11px] font-mono ${chipColorClass} hover:brightness-110`}
        data-testid={`voice-history-tool-expand-${idx}`}
        aria-expanded={expanded}
        aria-label={`tool ${tool.name}${expanded ? ' (expanded)' : ''}`}
      >
        <span data-testid={`voice-history-tool-chip-${idx}`}>
          [{labelInner}]{durationLabel}
        </span>
      </button>
      {expanded && (
        <div className="ml-4 mt-1 text-[11px] text-vibe-text-secondary font-mono">
          {tool.args_summary && (
            <div data-testid={`voice-history-tool-args-${idx}`}>
              <span className="text-vibe-text-tertiary">args:</span> {tool.args_summary}
            </div>
          )}
          {isError ? (
            <div
              data-testid={`voice-history-tool-error-${idx}`}
              className="text-vibe-error"
            >
              <span className="text-vibe-text-tertiary">error:</span> {tool.error}
            </div>
          ) : (
            tool.result_summary && (
              <div data-testid={`voice-history-tool-result-${idx}`}>
                <span className="text-vibe-text-tertiary">result:</span> {tool.result_summary}
              </div>
            )
          )}
        </div>
      )}
    </span>
  );
};
